import React, { useState, useEffect } from 'react';
import {
  Container, Grid, Card, Image, Text, Title, Group, Button,
  Stack, Badge, AspectRatio
} from '@mantine/core';
import { Link } from 'react-router-dom';
import { IconCalendar, IconUser, IconArrowRight } from '@tabler/icons-react';
import { useAppSelector } from '../../store/hooks';
import { SectionTitle } from '../common/SectionTitle';
import { galleryService } from '../../services/galleryService';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

const PLACEHOLDER_IMG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect width='100%25' height='100%25' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='%23999'%3ENo Image%3C/text%3E%3C/svg%3E";

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  image: string;
  author: string;
  date: string;
  category: string;
  slug: string;
}

// ✅ Static fallback (instant UI)
const staticBlogs: BlogPost[] = [
  {
    id: '1',
    title: 'The Rising Demand for Indian Agricultural Products in Global Markets',
    excerpt: 'Explore how Indian agricultural exports are gaining traction worldwide.',
    image: '/assets/images/blog/agricultural-exports.jpg',
    author: 'John Doe',
    date: '2024-01-15',
    category: 'Market Insights',
    slug: 'rising-demand-indian-agricultural-products'
  }
];

export const BlogSection: React.FC<{ showViewAll?: boolean }> = ({ showViewAll = true }) => {
  const { content } = useAppSelector((state) => state.content);
  const blogSection = content.pages.home.sections.blog;

  // ✅ Show instantly
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>(staticBlogs);

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        // 🔥 STEP 1: Cache check
        const cached = sessionStorage.getItem('blogs');
        if (cached) {
          setBlogPosts(JSON.parse(cached));
          return;
        }

        // 🔥 STEP 2: Fetch in background (no blocking)
        const gallery = await galleryService.getPublicGallery('BLOG');

        const items = gallery.items
          .filter(item => item.type === 'IMAGE' && item.active)
          .map(item => {
            let meta: any = {};
            try { meta = JSON.parse(item.metadata || '{}'); } catch(e) {}

            if (!meta.isBlogPost) return null;

            return {
              id: item.id,
              title: meta.title || item.title,
              excerpt: meta.excerpt || '',
              image: item.imageUrl,
              author: meta.author || '',
              date: meta.date || '',
              category: meta.category || '',
              slug: meta.slug || '',
            };
          })
          .filter((item): item is BlogPost => item !== null);

        if (items.length > 0) {
          setBlogPosts(items);
          sessionStorage.setItem('blogs', JSON.stringify(items));
        }

      } catch (err) {
        console.error('Blog fetch failed, using static');
      }
    };

    loadBlogs();
  }, []);

  const getImageUrl = (url: string) => {
    if (!url) return PLACEHOLDER_IMG;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/assets')) return url;
    return `${API_URL}${url}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const scrollToTop = () => window.scrollTo(0, 0);

  return (
    <Container size="xl">
      <SectionTitle title={blogSection.title} subtitle={blogSection.description} />

      <Grid gutter={30}>
        {blogPosts.slice(0, 3).map((blog) => (
          <Grid.Col key={blog.id} span={{ base: 12, md: 4 }}>
            <Card
              shadow="sm"
              padding="lg"
              radius="lg"
              withBorder
              style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              <Card.Section>
                <AspectRatio ratio={1}>
                  <Image
                    src={getImageUrl(blog.image)}
                    alt={blog.title}
                    fit="cover"
                    fallbackSrc={PLACEHOLDER_IMG}
                    loading="lazy"
                    decoding="async"
                  />
                </AspectRatio>
              </Card.Section>

              <Stack gap="md" style={{ flex: 1, marginTop: 16 }}>
                <Group justify="space-between">
                  <Badge color="primary" variant="light">
                    {blog.category}
                  </Badge>

                  <Group gap="xs">
                    <IconCalendar size={14} />
                    <Text size="xs" c="dimmed">
                      {formatDate(blog.date)}
                    </Text>
                  </Group>
                </Group>

                <Title order={3} size="h4" lineClamp={2}>
                  {blog.title}
                </Title>

                <Text size="sm" c="dimmed" lineClamp={3}>
                  {blog.excerpt}
                </Text>

                <Group gap="xs">
                  <IconUser size={14} />
                  <Text size="xs" c="dimmed">
                    By {blog.author}
                  </Text>
                </Group>

                <Button
                  component={Link}
                  to={`/blog/${blog.slug}`}
                  onClick={scrollToTop}
                  variant="subtle"
                  color="primary"
                  rightSection={<IconArrowRight size={16} />}
                  style={{ marginTop: 'auto' }}
                >
                  Read More
                </Button>
              </Stack>
            </Card>
          </Grid.Col>
        ))}
      </Grid>

      {showViewAll && blogPosts.length > 3 && (
        <Group justify="center" mt={40}>
          <Button
            component={Link}
            to="/blog"
            onClick={scrollToTop}
            size="lg"
            color="primary"
            variant="outline"
            rightSection={<IconArrowRight size={18} />}
          >
            View All Articles
          </Button>
        </Group>
      )}
    </Container>
  );
};